import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { caseStudies, caseStudyOrder } from "@/data/caseStudies";

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = caseStudies[slug];
  if (!cs) return <Navigate to="/" replace />;

  const idx = caseStudyOrder.indexOf(slug);
  const nextSlug = caseStudyOrder[(idx + 1) % caseStudyOrder.length];
  const next = caseStudies[nextSlug];

  return (
    <main data-testid={`case-study-${slug}`} className="pt-16">
      <div className="border-b border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-5xl"
        >
          <Link
            to="/"
            data-testid="case-study-back-link"
            className="mb-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-cyan-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All Projects
          </Link>
          <p className="mb-6 font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">{cs.tagline}</p>
          <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-white md:text-7xl">
            {cs.name}
          </h1>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3 border-t border-white/10 pt-6 font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">
            <span data-testid="case-study-role">{cs.role}</span>
            <span data-testid="case-study-status" className="text-cyan-400/80">{cs.status}</span>
          </div>
        </motion.div>
      </div>

      {cs.image && (
        <div className="border-b border-white/10 px-6 py-16 md:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="overflow-hidden border border-white/10"
          >
            <img src={cs.image} alt={`${cs.name} interface`} className="max-h-[70vh] w-full object-cover" />
          </motion.div>
        </div>
      )}

      <div className="px-6 py-24 md:px-12 md:py-32 lg:px-20">
        <div className="grid gap-16 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl text-base leading-relaxed text-zinc-300 md:text-lg"
            >
              {cs.overview}
            </motion.p>
            {cs.sections.map((s, si) => (
              <motion.div
                key={s.heading}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-16"
              >
                <h2 className="mb-8 flex items-baseline gap-4 font-display text-2xl font-black uppercase tracking-tight text-white md:text-3xl">
                  <span className="font-mono text-sm tracking-[0.3em] text-cyan-400">
                    [{String(si + 1).padStart(2, "0")}]
                  </span>
                  {s.heading}
                </h2>
                {s.paragraphs?.map((p, pi) => (
                  <p key={pi} className="mb-5 max-w-3xl text-sm leading-relaxed text-zinc-400 md:text-base">
                    {p}
                  </p>
                ))}
                {s.list && (
                  <ul className="space-y-3">
                    {s.list.map((item, ii) => (
                      <li key={ii} className="flex max-w-3xl gap-3 text-sm leading-relaxed text-zinc-400 md:text-base">
                        <span className="mt-2.5 h-px w-5 shrink-0 bg-cyan-400/50" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="border border-white/10 bg-[#0A0A0A] p-8">
              <h3 className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">{"//"} Stack</h3>
              <div className="flex flex-wrap gap-1.5">
                {cs.stack.map((t) => (
                  <span key={t} className="border border-white/10 px-2.5 py-1 font-mono text-[11px] text-zinc-400">
                    {t}
                  </span>
                ))}
              </div>
              <h3 className="mb-6 mt-10 font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">{"//"} Links</h3>
              <div className="flex flex-col gap-2">
                {cs.links.map((l) => (
                  <a
                    key={l.url}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`case-study-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group/l inline-flex items-center justify-between gap-2 border border-white/15 px-3 py-2 font-mono text-xs text-zinc-300 transition-colors duration-300 hover:border-cyan-400 hover:text-cyan-400"
                  >
                    {l.label}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/l:-translate-y-0.5 group-hover/l:translate-x-0.5" />
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Link
        to={`/projects/${nextSlug}`}
        data-testid="case-study-next-link"
        className="group block border-t border-white/10 px-6 py-16 transition-colors duration-500 hover:bg-white/[0.02] md:px-12 lg:px-20"
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Next case study</p>
        <div className="mt-4 flex items-center justify-between gap-6">
          <span className="font-display text-3xl font-black uppercase tracking-tighter text-white transition-colors duration-300 group-hover:text-cyan-400 md:text-5xl">
            {next.name}
          </span>
          <ArrowRight className="h-8 w-8 shrink-0 text-cyan-400 transition-transform duration-300 group-hover:translate-x-2" />
        </div>
      </Link>
    </main>
  );
}
